"use client";

import { useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, X, Loader2 } from "lucide-react";

interface CampaignImageUploadProps {
  id: string;
  label: string;
  value?: string;
  onChange: (url: string) => void;
  description?: string;
  campaignId?: string;
}

export function CampaignImageUpload({
  id,
  label,
  value,
  onChange,
  description,
  campaignId,
}: CampaignImageUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUpload = useCallback(async (file: File) => {
    setError(null);
    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);
      if (campaignId) {
        formData.append("campaignId", campaignId);
      }

      const response = await fetch("/api/campaigns/upload-image", {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Upload failed");
      }

      onChange(data.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setIsUploading(false);
    }
  }, [campaignId, onChange]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
      if (!allowedTypes.includes(file.type)) {
        setError("Invalid file type. Allowed: JPG, PNG, GIF, WebP");
        return;
      }
      handleUpload(file);
    }
  };

  const handleRemove = () => {
    onChange("");
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div>
      <Label htmlFor={id}>{label}</Label>

      <div className="mt-1">
        {/* Current image preview */}
        {value && (
          <div className="mb-3 relative">
            <div className="border rounded-lg overflow-hidden bg-gray-50">
              <div className="relative aspect-video w-full max-w-md">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={value}
                  alt="Campaign cover preview"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = "";
                    e.currentTarget.alt = "Failed to load";
                  }}
                />
              </div>
              <div className="p-2 flex justify-end bg-white border-t">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleRemove}
                  className="text-gray-500 hover:text-red-500"
                >
                  <X className="h-4 w-4 mr-1" />
                  Remove
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Upload area — hidden when an image is already set */}
        {!value && (
          <>
            <div
              className={`
                border-2 border-dashed rounded-lg p-6 text-center cursor-pointer
                transition-colors duration-200
                ${dragActive ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-gray-400"}
                ${isUploading ? "pointer-events-none opacity-50" : ""}
              `}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={handleClick}
            >
              <input
                ref={fileInputRef}
                id={id}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleFileChange}
                className="hidden"
              />

              {isUploading ? (
                <div className="flex flex-col items-center gap-2 py-4">
                  <Loader2 className="h-10 w-10 animate-spin text-blue-500" />
                  <span className="text-sm text-gray-600">Uploading...</span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2 py-4">
                  <Upload className="h-10 w-10 text-gray-400" />
                  <div className="text-sm">
                    <span className="text-blue-600 font-medium">Click to upload</span>
                    <span className="text-gray-500"> or drag and drop</span>
                  </div>
                  <span className="text-xs text-gray-400">
                    PNG, JPG, GIF, or WebP (max 10MB)
                  </span>
                  <span className="text-xs text-gray-400">
                    Recommended: 1200x630px (16:9 aspect ratio)
                  </span>
                </div>
              )}
            </div>

            {/* Description */}
            {description && (
              <p className="mt-1 text-xs text-gray-500">{description}</p>
            )}

            {/* Manual URL input (optional fallback) */}
            <div className="mt-3">
              <details className="text-xs">
                <summary className="text-gray-500 cursor-pointer hover:text-gray-700">
                  Or enter URL manually
                </summary>
                <Input
                  type="url"
                  value={value || ""}
                  onChange={(e) => onChange(e.target.value)}
                  className="mt-2"
                  placeholder="https://example.com/image.jpg"
                />
              </details>
            </div>
          </>
        )}

        {/* Error message */}
        {error && (
          <p className="mt-2 text-sm text-red-600">{error}</p>
        )}
      </div>
    </div>
  );
}
